import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useUrlState } from '../hooks/useUrlState';
import { api, User, ChargeoverAdmin } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Avatar, initials, Spinner, EmptyState, UserName, SortableTh } from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

const ROLE_OPTIONS: User['role'][] = ['IC', 'DM', 'SA', 'DIRECTOR', 'EXEC', 'OPS', 'SUPPORT'];
const BONUS_ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',   label: '— none —' },
  { value: 'IC', label: 'IC' },
  { value: 'DM', label: 'DM' },
  { value: 'SA', label: 'SA' },
];

const cellInputStyle: React.CSSProperties = {
  fontSize: 11,
  padding: '3px 6px',
  borderRadius: 'var(--radius-sm)',
  border: '0.5px solid var(--color-border-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
  minWidth: 0,
  width: '100%',
};

const bulkInputStyle: React.CSSProperties = {
  ...cellInputStyle,
  width: 140,
};

export default function AdminUsersPage() {
  const { data, isLoading, error, refetch } = useQuery(() => api.admin.users.list());
  const { user: authedUser, realUser, startImpersonation } = useAuth();
  // Holders of `impersonate` (without `admin_users`) get this page read-only
  // as the view-as target picker; the editors below and their supporting
  // lookups need the admin_users permission.
  const canEdit = authedUser?.permissions?.includes('admin_users') === true;
  // CO admins drive the chargeover_user_id dropdown. Loaded once on mount;
  // refresh by re-syncing from the Refresh page rather than re-fetching here
  // (the list is stable enough that re-fetching on every page open isn't
  // worth the round trip). Empty array on failure — the dropdown just won't
  // have options to choose from until the next sync.
  const { data: coAdminsData } = useQuery(
    () => (canEdit ? api.admin.chargeoverAdmins.list() : Promise.resolve({ admins: [] as ChargeoverAdmin[] })),
    [canEdit],
  );
  const coAdmins: ChargeoverAdmin[] = coAdminsData?.admins ?? [];
  // FP&A departments power the staff→department dropdown (admin-scoped list).
  const { data: fpaDeptData } = useQuery(
    () => (canEdit ? api.admin.fpaDepartments.list() : Promise.resolve({ departments: [] })),
    [canEdit],
  );
  const fpaDepartments = fpaDeptData?.departments ?? [];
  // Assignable permission profiles (role defaults apply by role and aren't
  // assignments). Defined on Admin → Permission profiles.
  const { data: profilesData } = useQuery(
    () => (canEdit ? api.admin.permissionProfiles.list() : Promise.resolve({ permissions: [], scopes: [], profiles: [] })),
    [canEdit],
  );
  const assignableProfiles = (profilesData?.profiles ?? []).filter((p) => !p.system_role);
  const navigate = useNavigate();
  const [saving, setSaving] = useState<string | null>(null);
  const [localEdits, setLocalEdits] = useState<Record<string, Partial<User>>>({});

  const allUsers = data?.users ?? [];
  const [showInactive, setShowInactive] = useUrlState('inactive', '0');
  const showingInactive = showInactive === '1';

  const filteredUsers = useMemo(
    () => (showingInactive ? allUsers : allUsers.filter((u) => u.is_active)),
    [allUsers, showingInactive],
  );

  const { sorted: users, sort } = useTableSort(
    filteredUsers,
    {
      name:    (u) => u.display_name,
      title:   (u) => u.title ?? '',
      role:    (u) => u.role,
      manager: (u) => u.manager_jira_user_id ?? '',
      team:    (u) => u.team ?? '',
      admin:   (u) => u.is_admin,
      billing: (u) => u.is_billing_admin,
      status:  (u) => u.is_active,
    },
    { key: 'name', dir: 'asc' },
  );
  const inactiveCount = allUsers.length - allUsers.filter((u) => u.is_active).length;

  const usersById = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of allUsers) map.set(u.jira_user_id, u);
    return map;
  }, [allUsers]);

  // ── Selection & bulk edit state ──────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkRole, setBulkRole] = useState<User['role']>('IC');
  const [bulkTitle, setBulkTitle] = useState('');
  const [bulkTeam, setBulkTeam] = useState('');
  const [bulkManager, setBulkManager] = useState<string>('');

  const visibleIds = useMemo(() => users.map((u) => u.jira_user_id), [users]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = visibleIds.some((id) => selected.has(id));

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleSelectAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  async function applyBulk(patch: Partial<User>, label: string) {
    const ids = [...selected];
    if (!ids.length) return;
    if (!confirm(`Apply ${label} to ${ids.length} user${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkSaving(true);
    try {
      // Parallel is fine for the dev pglite; in prod Postgres with N=hundreds
      // we'd want to cap concurrency. Good enough for now.
      await Promise.all(ids.map((id) => api.admin.users.update(id, patch)));
      refetch();
    } catch (e) {
      alert(`Bulk update failed: ${(e as Error).message}`);
    } finally {
      setBulkSaving(false);
    }
  }

  async function impersonate(target: User) {
    try {
      await startImpersonation(target);
      navigate('/scorecard');
    } catch (e) {
      alert(`Failed to start impersonation: ${(e as Error).message}`);
    }
  }

  function editUser(id: string, patch: Partial<User>) {
    setLocalEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  async function saveUser(userId: string) {
    const patch = localEdits[userId];
    if (!patch) return;
    setSaving(userId);
    try {
      await api.admin.users.update(userId, patch);
      setLocalEdits((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      refetch();
    } catch (e) {
      alert(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(null);
    }
  }

  function userValue<K extends keyof User>(u: User, key: K): User[K] {
    const edit = localEdits[u.jira_user_id];
    if (edit && key in edit) return edit[key] as User[K];
    return u[key];
  }

  const isDirty = (id: string) => !!localEdits[id] && Object.keys(localEdits[id]).length > 0;
  const hasSelection = selected.size > 0;

  // Read-only variant for `impersonate`-without-`admin`: just the roster and
  // a view-as action. All edit endpoints are admin-gated server-side anyway.
  if (!canEdit) {
    return (
      <>
        <SectionLabel>Users</SectionLabel>
        <Card>
          <CardHeader>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Pick a user to view the app as</span>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
              read-only — editing needs the Users &amp; roles permission
            </span>
          </CardHeader>
          {isLoading ? <Spinner />
            : error ? <EmptyState message={String(error)} />
            : (
              <Table>
                <thead><tr><Th>Name</Th><Th>Title</Th><Th>Role</Th><Th>Team</Th><Th>{''}</Th></tr></thead>
                <tbody>
                  {users.map((u, i) => (
                    <Tr key={u.jira_user_id}>
                      <Td bold>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Avatar initials={initials(u.display_name)} size={24} index={i} />
                          <UserName name={u.display_name} active={u.is_active} />
                        </div>
                      </Td>
                      <Td muted>{u.title || '—'}</Td>
                      <Td muted>{u.role}</Td>
                      <Td muted>{u.team || '—'}</Td>
                      <Td>
                        <ActionButton
                          onClick={() => impersonate(u)}
                          disabled={u.jira_user_id === realUser?.jira_user_id}
                        >
                          View as ↗
                        </ActionButton>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
        </Card>
      </>
    );
  }

  return (
    <>
      <SectionLabel>User management</SectionLabel>
      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            {showingInactive ? 'All users' : 'Active users'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            {users.length} shown
            {inactiveCount > 0 && !showingInactive ? ` · ${inactiveCount} deactivated hidden` : ''}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <ActionButton
              onClick={() => setShowInactive(showingInactive ? '0' : '1')}
              variant="default"
            >
              {showingInactive ? 'Hide deactivated' : `Show deactivated${inactiveCount > 0 ? ` (${inactiveCount})` : ''}`}
            </ActionButton>
            <ActionButton onClick={refetch} variant="default">Sync from Jira</ActionButton>
          </div>
        </CardHeader>

        {hasSelection && (
          <div
            style={{
              padding: '8px 16px',
              background: 'var(--color-blue-bg)',
              borderBottom: '0.5px solid var(--color-border-strong)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
            }}
          >
            <strong style={{ color: 'var(--color-blue-text)' }}>
              {selected.size} selected
            </strong>

            <span style={{ display: 'inline-flex', gap: 4, marginLeft: 4 }}>
              <ActionButton
                onClick={() => applyBulk({ is_active: true }, 'Activate')}
                variant="default"
                disabled={bulkSaving}
              >
                Activate
              </ActionButton>
              <ActionButton
                onClick={() => applyBulk({ is_active: false }, 'Deactivate')}
                variant="danger"
                disabled={bulkSaving}
              >
                Deactivate
              </ActionButton>
            </span>

            <span style={{ borderLeft: '0.5px solid var(--color-border-strong)', height: 18, margin: '0 4px' }} />

            <label style={{ color: 'var(--color-text-muted)' }}>Role</label>
            <select
              value={bulkRole}
              onChange={(e) => setBulkRole(e.target.value as User['role'])}
              style={bulkInputStyle}
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <ActionButton
              onClick={() => applyBulk({ role: bulkRole }, `Role = ${bulkRole}`)}
              variant="primary"
              disabled={bulkSaving}
            >
              Apply
            </ActionButton>

            <label style={{ color: 'var(--color-text-muted)' }}>Title</label>
            <input
              type="text"
              value={bulkTitle}
              onChange={(e) => setBulkTitle(e.target.value)}
              placeholder="e.g. Developer"
              style={bulkInputStyle}
            />
            <ActionButton
              onClick={() => applyBulk({ title: bulkTitle }, `Title = "${bulkTitle}"`)}
              variant="primary"
              disabled={bulkSaving || !bulkTitle.trim()}
            >
              Apply
            </ActionButton>

            <label style={{ color: 'var(--color-text-muted)' }}>Team</label>
            <input
              type="text"
              value={bulkTeam}
              onChange={(e) => setBulkTeam(e.target.value)}
              placeholder="e.g. Delivery"
              style={bulkInputStyle}
            />
            <ActionButton
              onClick={() => applyBulk({ team: bulkTeam }, `Team = "${bulkTeam}"`)}
              variant="primary"
              disabled={bulkSaving || !bulkTeam.trim()}
            >
              Apply
            </ActionButton>

            <label style={{ color: 'var(--color-text-muted)' }}>Manager</label>
            <select
              value={bulkManager}
              onChange={(e) => setBulkManager(e.target.value)}
              style={bulkInputStyle}
            >
              <option value="">— none —</option>
              {allUsers.map((m) => (
                <option key={m.jira_user_id} value={m.jira_user_id}>
                  {m.display_name}{m.title ? ` · ${m.title}` : ''}
                </option>
              ))}
            </select>
            <ActionButton
              onClick={() =>
                applyBulk(
                  { manager_jira_user_id: bulkManager || null },
                  `Manager = ${bulkManager ? (usersById.get(bulkManager)?.display_name ?? bulkManager) : 'none'}`,
                )
              }
              variant="primary"
              disabled={bulkSaving}
            >
              Apply
            </ActionButton>

            <div style={{ marginLeft: 'auto' }}>
              <ActionButton onClick={clearSelection} variant="default">
                Clear selection
              </ActionButton>
            </div>
          </div>
        )}

        {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : (
          <Table>
            <thead>
              <tr>
                <Th>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                    onChange={toggleSelectAllVisible}
                    title={allVisibleSelected ? 'Deselect all' : 'Select all visible'}
                  />
                </Th>
                <SortableTh columnKey="name"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Name</SortableTh>
                <SortableTh columnKey="title"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Title</SortableTh>
                <SortableTh columnKey="role"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Role</SortableTh>
                <SortableTh columnKey="manager" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Manager</SortableTh>
                <SortableTh columnKey="team"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Team</SortableTh>
                <Th>FP&amp;A dept</Th>
                <Th>CO user</Th>
                <SortableTh columnKey="admin"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Admin</SortableTh>
                <SortableTh columnKey="billing" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Billing</SortableTh>
                <SortableTh columnKey="status"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const dirty = isDirty(u.jira_user_id);
                const active = userValue(u, 'is_active');
                const admin = userValue(u, 'is_admin');
                const billingAdmin = userValue(u, 'is_billing_admin');
                const managerId = userValue(u, 'manager_jira_user_id') ?? '';
                const isSelected = selected.has(u.jira_user_id);
                return (
                  <Tr key={u.jira_user_id} selected={isSelected}>
                    <Td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(u.jira_user_id)}
                      />
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar initials={initials(u.display_name)} size={24} index={i} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500 }}>
                            <UserName name={u.display_name} active={active} />
                          </div>
                          <input
                            type="email"
                            value={userValue(u, 'email') ?? ''}
                            onChange={(e) => editUser(u.jira_user_id, { email: e.target.value })}
                            style={{
                              ...cellInputStyle,
                              fontSize: 11,
                              color: 'var(--color-text-muted)',
                              padding: '2px 4px',
                              marginTop: 2,
                              width: '100%',
                            }}
                            placeholder="Email (used for Google SSO match)"
                          />
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <input
                        type="text"
                        value={userValue(u, 'title') ?? ''}
                        onChange={(e) => editUser(u.jira_user_id, { title: e.target.value })}
                        style={cellInputStyle}
                        placeholder="e.g. Developer"
                      />
                    </Td>
                    <Td>
                      <select
                        value={userValue(u, 'role')}
                        onChange={(e) => editUser(u.jira_user_id, { role: e.target.value as User['role'] })}
                        style={cellInputStyle}
                      >
                        {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {(() => {
                        const r = userValue(u, 'role');
                        // Player/coach bonus override makes sense for directors/execs.
                        // For IC/DM/SA it's redundant with `role` so we hide it to
                        // keep the table clean.
                        if (r !== 'DIRECTOR' && r !== 'EXEC') return null;
                        const br = userValue(u, 'bonus_role') ?? '';
                        return (
                          <select
                            value={br}
                            onChange={(e) =>
                              editUser(u.jira_user_id, {
                                bonus_role: (e.target.value || null) as User['bonus_role'],
                              })
                            }
                            style={{ ...cellInputStyle, marginTop: 4, fontSize: 10 }}
                            title="Player/coach bonus override"
                          >
                            {BONUS_ROLE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.value === '' ? 'no bonus' : `+ ${o.label} bonus`}
                              </option>
                            ))}
                          </select>
                        );
                      })()}
                      {/* Additional permission profiles on top of the role
                          default (union of all). Managed on Permission profiles. */}
                      {assignableProfiles.length > 0 && (() => {
                        const ids = new Set(userValue(u, 'permission_profile_ids') ?? []);
                        return (
                          <details style={{ marginTop: 4 }}>
                            <summary style={{ fontSize: 10, color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                              {ids.size > 0 ? `+${ids.size} permission profile${ids.size === 1 ? '' : 's'}` : 'permission profiles'}
                            </summary>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                              {assignableProfiles.map((p) => (
                                <label key={p.id} style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} title={p.description ?? undefined}>
                                  <input
                                    type="checkbox"
                                    checked={ids.has(p.id)}
                                    onChange={(e) => {
                                      const next = new Set(ids);
                                      if (e.target.checked) next.add(p.id); else next.delete(p.id);
                                      editUser(u.jira_user_id, { permission_profile_ids: [...next] });
                                    }}
                                  />
                                  {p.name}
                                </label>
                              ))}
                            </div>
                          </details>
                        );
                      })()}
                    </Td>
                    <Td>
                      <select
                        value={managerId}
                        onChange={(e) => editUser(u.jira_user_id, { manager_jira_user_id: e.target.value || null })}
                        style={cellInputStyle}
                      >
                        <option value="">— none —</option>
                        {allUsers
                          .filter((m) => m.jira_user_id !== u.jira_user_id)
                          .map((m) => (
                            <option key={m.jira_user_id} value={m.jira_user_id}>
                              {m.display_name}{m.title ? ` · ${m.title}` : ''}
                            </option>
                          ))}
                      </select>
                      {managerId && !usersById.has(managerId) && (
                        <div style={{ fontSize: 10, color: 'var(--color-red-text)', marginTop: 2 }}>
                          Manager not in list
                        </div>
                      )}
                    </Td>
                    <Td>
                      <input
                        type="text"
                        value={userValue(u, 'team') ?? ''}
                        onChange={(e) => editUser(u.jira_user_id, { team: e.target.value })}
                        style={cellInputStyle}
                        placeholder="Team"
                      />
                    </Td>
                    <Td>
                      {/* FP&A department membership — ties this person to the
                          FP&A team structure (drives department-scoped FP&A). */}
                      <select
                        value={userValue(u, 'fpa_department_id') ?? ''}
                        onChange={(e) => editUser(u.jira_user_id, { fpa_department_id: e.target.value || null })}
                        style={cellInputStyle}
                        title="FP&A department this person belongs to"
                      >
                        <option value="">— none —</option>
                        {fpaDepartments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 10, marginTop: 4 }}
                        title="Contractor: variable cost (billed, not salaried) and NOT bonus-eligible.">
                        <input
                          type="checkbox"
                          checked={!!userValue(u, 'is_contractor')}
                          onChange={(e) => editUser(u.jira_user_id, { is_contractor: e.target.checked })}
                        />
                        Contractor
                      </label>
                    </Td>
                    <Td>
                      {/* ChargeOver user (sales rep) override. The auto-link
                          fills this from the matching email at sync time;
                          changing the value here is a manual override that
                          the next sync's WHERE NULL clause leaves alone. */}
                      <select
                        value={userValue(u, 'chargeover_user_id') ?? ''}
                        onChange={(e) => editUser(u.jira_user_id, { chargeover_user_id: e.target.value || null })}
                        style={cellInputStyle}
                        title={(() => {
                          const id = userValue(u, 'chargeover_user_id');
                          if (!id) return 'No ChargeOver user mapped';
                          const co = coAdmins.find((a) => a.chargeover_admin_id === id);
                          return co ? `${co.display_name ?? id}${co.email ? ` · ${co.email}` : ''}` : `Unknown CO id: ${id}`;
                        })()}
                      >
                        <option value="">— none —</option>
                        {coAdmins.map((a) => (
                          <option key={a.chargeover_admin_id} value={a.chargeover_admin_id}>
                            {a.display_name ?? a.email ?? a.chargeover_admin_id}
                          </option>
                        ))}
                      </select>
                    </Td>
                    <Td>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                        <input
                          type="checkbox"
                          checked={admin}
                          onChange={(e) => editUser(u.jira_user_id, { is_admin: e.target.checked })}
                        />
                        {admin ? 'Yes' : 'No'}
                      </label>
                    </Td>
                    <Td>
                      <label
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}
                        title="Grants access to the ChargeOver billing workflow. Admins implicitly have access regardless of this flag."
                      >
                        <input
                          type="checkbox"
                          checked={billingAdmin}
                          onChange={(e) => editUser(u.jira_user_id, { is_billing_admin: e.target.checked })}
                        />
                        {billingAdmin ? 'Yes' : 'No'}
                      </label>
                    </Td>
                    <Td>
                      <Badge variant={active ? 'green' : 'gray'}>{active ? 'Active' : 'Inactive'}</Badge>
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {dirty && (
                          <ActionButton
                            onClick={() => saveUser(u.jira_user_id)}
                            variant="primary"
                          >
                            {saving === u.jira_user_id ? 'Saving…' : 'Save'}
                          </ActionButton>
                        )}
                        <ActionButton
                          onClick={() => editUser(u.jira_user_id, { is_active: !active })}
                          variant={active ? 'danger' : 'default'}
                        >
                          {active ? 'Deactivate' : 'Activate'}
                        </ActionButton>
                        <ActionButton
                          onClick={() => impersonate(u)}
                          variant="default"
                          disabled={u.jira_user_id === realUser?.jira_user_id}
                        >
                          Impersonate ↗
                        </ActionButton>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
